/**
 * UserManager - Менеджер для управління користувачами та авторизацією
 *
 * Централізоване управління:
 * - Авторизацією та деавторизацією користувачів
 * - Інформацією про поточного користувача
 * - JWT та Refresh токенами
 * - User options (налаштування користувача)
 * - Відновленням пароля
 *
 * Базується на старому коді з:
 * - src.old/Core/Stores/UserStore.js
 * - src.old/Core/Stores/UserOptionStore.js
 * - src.old/Core/Components/Auth/LoginForm/LoginForm.jsx
 * - src.old/Core/Components/Auth/ForgotPasswordForm/ForgotPasswordForm.jsx
 */

import pkg from 'crypto-js'
import { makeAutoObservable, reaction, toJS } from 'mobx'
import { makePersistable } from 'mobx-persist-store'
const { enc, AES } = pkg

import { configManager } from '@/Core/Managers/Config'
import { crudResourceRegistry } from '@/Core/Services/CrudResourceRegistry'
import { navigationService } from '@/Core/Services/NavigationService'
import { restClient } from '@/Core/Services/RestClientService'
import { defaultAuthStrategy } from '@/Core/Strategies/Auth/DefaultAuthStrategy'

import type { IResolvedUserOption, UserManagerApiConfig } from './UserManager.types'
import type { IAuthContext, IAuthStrategy } from '@/Core/Contracts/Auth/IAuthStrategy'
import type { IAppConfig } from '@/Core/Contracts/Configs/IAppConfig'
import type {
    IForgotPasswordData,
    IForgotPasswordResult,
    ILoginData,
    ILoginOptions,
    ILoginResult,
    IRefreshTokenResponse,
    IResetPasswordData,
    IResetPasswordResult,
    IUser,
    IUserManager,
    IUserManagerOptions,
} from '@/Core/Contracts/Managers/IUserManager'

/**
 * Отримує секретний ключ для шифрування з змінних оточення
 * @returns секретний ключ
 * @throws Error якщо ключ не вказано
 */
function getEncryptionKey(): string {
    // Спробуємо отримати ключ з import.meta.env (клієнт, Vite)
    const env = (import.meta as any).env
    if (typeof env !== 'undefined' && env?.VITE_USER_ENCRYPTION_SECRET_KEY) {
        return env.VITE_USER_ENCRYPTION_SECRET_KEY
    }

    // Спробуємо отримати ключ з process.env (сервер)
    if (typeof process !== 'undefined' && process.env?.['VITE_USER_ENCRYPTION_SECRET_KEY']) {
        return process.env['VITE_USER_ENCRYPTION_SECRET_KEY']
    }

    throw new Error(
        '[UserManager] VITE_USER_ENCRYPTION_SECRET_KEY is not defined in environment variables. ' +
            'Please add it to your .env file.'
    )
}

/**
 * Секретний ключ для шифрування даних
 * Використовується для шифрування чутливих даних в localStorage
 */
const SECRET_KEY = getEncryptionKey()

/**
 * Шифрує дані за допомогою AES
 * @param data - дані для шифрування
 * @returns зашифрований рядок
 */
function encrypt(data: any): string {
    return AES.encrypt(JSON.stringify(data), SECRET_KEY).toString()
}

/**
 * Розшифровує дані, зашифровані за допомогою AES
 * @param data - зашифрований рядок
 * @returns розшифровані дані
 */
function decrypt(data: string): any {
    try {
        const bytes = AES.decrypt(data, SECRET_KEY)
        const string = bytes.toString(enc.Utf8)

        if (!string) {
            return {}
        }

        return JSON.parse(string)
    } catch {
        // Data is encrypted with a different key or is corrupted.
        // Clear all UserManager keys from localStorage to force fresh login.
        try {
            Object.keys(window.localStorage)
                .filter((k) => k.startsWith('UserManager'))
                .forEach((k) => window.localStorage.removeItem(k))
        } catch {
            // localStorage not available — ignore
        }
        return {}
    }
}

/**
 * Клас UserManager
 * Реалізує інтерфейс IUserManager та керує всіма аспектами роботи з користувачами
 */
export class UserManager implements IUserManager {
    /** Чи авторизований користувач */
    isAuthorized: boolean = false

    /** Дані користувача */
    user: IUser = {}

    /** JWT токен доступу */
    token: string | null = null

    /** Centrifugo токен для подключения */
    centrifugoConnectionToken: string | null = null
    centrifugoConnectionTokenExpiration: string | null = null
    centrifugoSubscriptionToken: string | null = null
    centrifugoSubscriptionTokenExpiration: string | null = null

    /** Refresh токен для оновлення JWT */
    refreshToken: string | null = null

    /** Час закінчення дії refresh токена */
    refreshTokenExpiration: string | null = null

    /** Примусова зміна пароля */
    mustChangePassword: boolean = false

    /** User options (налаштування користувача) */
    userOptions: Record<string, any> = {}

    /** Режим налагодження */
    private debug: boolean

    /** Стратегія авторізації */
    private authStrategy: IAuthStrategy

    /** Прапорець для запобігання множинним одночасним викликам refreshAccessToken */
    private isRefreshing: boolean = false

    /**
     * Shared promise для черги одночасних викликів refreshAccessToken
     * Дозволяє декільком concurrent запитам дочекатися одного рефрешу замість повернення false
     */
    private refreshPromise: Promise<boolean> | null = null

    /** Прапорець чи завершена гідратація даних з localStorage */
    private isHydrated: boolean = false

    /**
     * Shared promise очікування гідратації: всі конкурентні waitForHydration
     * чекають один таймер і резолвляться в одному тіку. Це критично для
     * склеювання resolved-запитів — continuation'и викликачів потрапляють
     * в одне setTimeout(0)-вікно, а не в різні макротаски окремих інтервалів.
     */
    private hydrationPromise: Promise<boolean> | null = null

    /** Коди, накопичені до наступного флашу склеєного resolved-запиту */
    private pendingResolveCodes = new Set<string>()

    /** Promise поточного (ще не флашнутого) батча resolved-запиту */
    private resolveFlushPromise: Promise<void> | null = null

    /** Дедуп inflight-резолвів: код -> promise батча, який його завантажує */
    private inflightResolves = new Map<string, Promise<void>>()

    /** Ключ для збереження user options */
    private userOptionsKey: string

    /** Ідентифікатор користувача, чий бакет опцій зараз підключено */
    private userOptionsOwner: string | null = null

    /** Disposer реакції збереження userOptions у бакет поточного користувача */
    private userOptionsPersistDisposer: (() => void) | null = null

    constructor(options: IUserManagerOptions = {}) {
        this.debug = options.debug ?? false
        this.userOptionsKey = options.userOptionsKey ?? ''
        this.authStrategy = options.authStrategy ?? defaultAuthStrategy

        // Робимо клас реактивним через MobX
        // refreshPromise та службові поля персистенсу/резолву опцій виключено з реактивності
        makeAutoObservable(this, {
            refreshPromise: false,
            userOptionsOwner: false,
            userOptionsPersistDisposer: false,
            hydrationPromise: false,
            pendingResolveCodes: false,
            resolveFlushPromise: false,
            inflightResolves: false,
        })

        // Ініціалізуємо персистентність тільки в браузері
        if (typeof window !== 'undefined') {
            this.initPersistence()
        }
    }

    /**
     * Перевіряє чи можна розшифрувати збережені дані поточним ключем.
     * Якщо ні — очищає всі ключі UserManager з localStorage.
     * Викликається перед makePersistable щоб уникнути Malformed UTF-8 помилки.
     */
    private clearCorruptedStorage(): void {
        const encryptedKeys = ['refreshToken', 'refreshTokenExpiration', 'user']
        try {
            const raw = window.localStorage.getItem('UserManager')
            if (!raw) return

            const parsed = JSON.parse(raw)
            for (const key of encryptedKeys) {
                const val = parsed[key]
                if (!val) continue
                try {
                    decrypt(val)
                } catch {
                    // Decryption failed — wipe all UserManager entries
                    Object.keys(window.localStorage)
                        .filter((k) => k.startsWith('UserManager'))
                        .forEach((k) => window.localStorage.removeItem(k))
                    return
                }
            }
        } catch {
            // localStorage parse error — also wipe
            Object.keys(window.localStorage)
                .filter((k) => k.startsWith('UserManager'))
                .forEach((k) => window.localStorage.removeItem(k))
        }
    }

    /**
     * Ініціалізує персистентність даних в localStorage
     * Шифрує чутливі дані (токени, дані користувача)
     */
    private async initPersistence(): Promise<void> {
        // Validate existing localStorage data before hydration.
        // If any encrypted field fails to decrypt (wrong key / corrupted data),
        // wipe all UserManager keys so makePersistable starts from a clean state.
        this.clearCorruptedStorage()

        try {
            await makePersistable(this, {
                name: 'UserManager',
                properties: [
                    'isAuthorized',
                    'mustChangePassword',
                    'token',
                    'centrifugoConnectionToken',
                    'centrifugoConnectionTokenExpiration',
                    'centrifugoSubscriptionToken',
                    'centrifugoSubscriptionTokenExpiration',
                    {
                        key: 'refreshToken',
                        serialize: (value: any) => encrypt(value),
                        deserialize: (value: string) => decrypt(value),
                    },
                    {
                        key: 'refreshTokenExpiration',
                        serialize: (value: any) => encrypt(value),
                        deserialize: (value: string) => decrypt(value),
                    },
                    {
                        key: 'user',
                        serialize: (value: any) => encrypt(toJS(value)),
                        deserialize: (value: string) => decrypt(value),
                    },
                ],
                storage: window.localStorage,
            })

            // Легасі: спільний для всіх користувачів бакет опцій — видаляємо,
            // щоб налаштування одного користувача не «протікали» іншому
            window.localStorage.removeItem(`UserManagerOptions${this.userOptionsKey}`)

            // Підключаємо окремий бакет опцій користувача (user вже гідратований вище)
            this.attachUserOptionsPersistence()

            this.isHydrated = true
        } catch (error) {
            console.error('[UserManager] Hydration error:', error)
            this.isHydrated = true // Встановлюємо в true навіть при помилці, щоб не блокувати роботу
        }
    }

    /**
     * Ідентифікатор поточного користувача для нейсмпейсу бакета опцій
     */
    private getCurrentUserId(): string | null {
        const id = this.user?.['uuid'] ?? this.user?.id
        return id !== undefined && id !== null ? String(id) : null
    }

    /**
     * Ім'я localStorage-бакета опцій конкретного користувача
     */
    private getUserOptionsStorageName(owner: string): string {
        return `UserManagerOptions${this.userOptionsKey}_${owner}`
    }

    /**
     * Підключає персистентність userOptions до бакета поточного користувача.
     * Окремий localStorage-ключ на користувача виключає «протікання» опцій між
     * користувачами (login без logout, кілька вкладок, незафлушений clear()).
     * Викликається після гідратації (initPersistence) та після login().
     */
    private attachUserOptionsPersistence(): void {
        const owner = this.getCurrentUserId()
        if (owner === this.userOptionsOwner) return

        const previousOwner = this.userOptionsOwner
        this.detachUserOptionsPersistence()
        this.userOptionsOwner = owner

        if (!owner) {
            this.userOptions = {}
            return
        }

        const storageName = this.getUserOptionsStorageName(owner)

        let storedOptions: Record<string, any> = {}
        try {
            storedOptions = JSON.parse(window.localStorage.getItem(storageName) ?? '{}')
        } catch {
            storedOptions = {}
        }
        this.userOptions = storedOptions

        this.userOptionsPersistDisposer = reaction(
            () => JSON.stringify(toJS(this.userOptions)),
            (serialized) => {
                try {
                    window.localStorage.setItem(storageName, serialized)
                } catch {
                    // localStorage недоступний/переповнений — працюємо без локального кешу
                }
            }
        )

        // Інший користувач у тій самій вкладці — скидаємо кешовані CRUD-контексти,
        // щоб onAfterInit-features (зокрема завантаження налаштувань) відпрацювали заново
        if (previousOwner && previousOwner !== owner) {
            try {
                crudResourceRegistry.clearAll()
            } catch {
                // registry ще не ініціалізований — нічого чистити
            }
        }
    }

    /**
     * Відключає персистентність userOptions. Бакет у localStorage не чистимо —
     * локальний кеш користувача переживає logout і відновлюється при поверненні.
     */
    private detachUserOptionsPersistence(): void {
        if (this.userOptionsPersistDisposer) {
            this.userOptionsPersistDisposer()
            this.userOptionsPersistDisposer = null
        }
        this.userOptionsOwner = null
    }

    /**
     * Очікує завершення гідратації даних з localStorage
     * @param timeout - максимальний час очікування в мс (за замовчуванням 5000)
     * @returns Promise, який розв'язується коли гідратація завершена
     */
    async waitForHydration(timeout: number = 5000): Promise<boolean> {
        if (this.isHydrated) {
            return true
        }

        // Один спільний таймер на всіх конкурентних чекачів: усі проміси резолвляться
        // в одному тіку, тож подальші виклики (напр. resolveUserOptions від різних
        // crud-компонентів) потрапляють в одне вікно склеювання. timeout застосовується
        // з першого виклику, що створив проміс.
        if (!this.hydrationPromise) {
            const startTime = Date.now()

            this.hydrationPromise = new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (this.isHydrated) {
                        clearInterval(checkInterval)
                        this.hydrationPromise = null
                        resolve(true)
                    } else if (Date.now() - startTime > timeout) {
                        clearInterval(checkInterval)
                        this.hydrationPromise = null
                        console.warn('[UserManager] Hydration timeout exceeded')
                        resolve(false)
                    }
                }, 50)
            })
        }

        return this.hydrationPromise
    }

    /**
     * Отримує конфігурацію API з AppConfig
     * @returns конфігурація URL-адрес для API
     * @throws Error якщо будь-які обов'язкові поля відсутні в конфігурації
     */
    private getApiConfig(): UserManagerApiConfig {
        const appConfig = configManager.get<IAppConfig>('AppConfig')

        if (!appConfig) {
            throw new Error('[UserManager] AppConfig is not loaded')
        }

        // Перевіряємо наявність всіх обов'язкових полів
        const requiredFields = [
            'restLoginUrl',
            'restUserMe',
            'restForgotPasswordUrl',
            'restRefreshTokenUrl',
            'restUserOptionUrl',
        ] as const

        const missingFields: string[] = []

        for (const field of requiredFields) {
            if (!appConfig[field]) {
                missingFields.push(field)
            }
        }

        if (missingFields.length > 0) {
            throw new Error(`[UserManager] Missing required fields in AppConfig: ${missingFields.join(', ')}`)
        }

        return {
            loginUrl: appConfig.restLoginUrl,
            userMeUrl: appConfig.restUserMe,
            forgotPasswordUrl: appConfig.restForgotPasswordUrl,
            resetPasswordUrl: appConfig.restResetPasswordUrl || '/services/users/{token}/password_reset',
            refreshTokenUrl: appConfig.restRefreshTokenUrl,
            userOptionUrl: appConfig.restUserOptionUrl,
            resolvedUserOptionUrl: appConfig.restResolvedUserOptionUrl || `${appConfig.restUserOptionUrl}/resolved`,
            optionUrl: appConfig.restOptionUrl || '/services/options',
            backendUrl: appConfig.backendUrl || '',
        }
    }

    /**
     * Авторизація користувача
     * @param data - дані для авторизації (логін і пароль)
     * @param options - додаткові параметри (стратегія авторізації, параметри стратегії)
     * @returns результат авторізації з інформацією про помилки
     */
    async login(data: ILoginData, options?: ILoginOptions): Promise<ILoginResult> {
        try {
            const apiConfig = this.getApiConfig()

            // Вибираємо стратегію: з параметрів, з конструктора або дефолтну
            const strategy = options?.authStrategy ?? this.authStrategy

            // Створюємо контекст для стратегії
            const context: IAuthContext = {
                get: async <T = any>(url: string, opts?: any) => {
                    const result = (await restClient.get<T>(url, opts)) as unknown as T | null
                    
                    if (result === null) {
                        throw new Error('Request failed - no response from server')
                    }
                    
                    return result
                },
                post: async <T = any>(url: string, opts?: any) => {
                    // Завжди додаємо isPublic: true для публічних запитів авторизації
                    const postOpts = { ...opts }
                    const result = (await restClient.post<T>(url, postOpts, { isPublic: true })) as unknown as T | null
                    
                    if (result === null) {
                        throw new Error('Request failed - no response from server')
                    }
                    
                    return result
                },
                ...apiConfig,
            }

            // Виконуємо авторізацію через стратегію
            const result = await strategy.execute(data, context, options?.authStrategyOptions)

            if (!result.success) {
                return {
                    success: false,
                    error: result.error || 'Authentication failed',
                    errorType: result.errorType || 'server',
                }
            }

            // Перевіряємо наявність токена
            if (!result.token) {
                return {
                    success: false,
                    error: 'No token received',
                    errorType: 'server',
                }
            }

            // Зберігаємо дані користувача та токени
            this.setIsAuthorized(true)
            this.mustChangePassword = result.mustChangePassword ?? false
            this.setToken(result.token)
            this.setCentrifugoConnectionToken(result.centrifugoConnectionToken ?? null)
            this.setCentrifugoConnectionTokenExpiration(result.centrifugoConnectionTokenExpiration ?? null)
            this.setCentrifugoSubscriptionToken(result.centrifugoSubscriptionToken ?? null)
            this.setCentrifugoSubscriptionTokenExpiration(result.centrifugoSubscriptionTokenExpiration ?? null)

            if (result.refreshToken) {
                this.setRefreshToken(result.refreshToken)
            }

            if (result.refreshTokenExpiration) {
                this.setRefreshTokenExpiration(result.refreshTokenExpiration)
            }

            if (result.user) {
                this.setUser(result.user)
            }

            // Перемикаємо персистентність опцій на бакет нового користувача
            // (покриває і login іншим користувачем без попереднього logout)
            this.attachUserOptionsPersistence()

            return {
                success: true,
            }
        } catch (error: any) {
            if (this.debug) {
                console.error('[UserManager] Login error:', error)
            }

            return {
                success: false,
                error: error?.message || 'Unknown error',
                errorType: 'server',
            }
        }
    }    /**
     * Вихід з системи
     * Очищає всі дані користувача та токени
     * Використовує React Router для навігації без перезавантаження сторінки
     * @param skipRedirect - якщо true, не робить переадресацію на сторінку авторизації
     */
    logout(skipRedirect: boolean = false): void {
        this.clear()

        // Перенаправляємо на сторінку авторизації, тільки якщо не вимкнена переадресація
        if (!skipRedirect && typeof window !== 'undefined') {
            const appConfig = configManager.get<IAppConfig>('AppConfig')
            const authPageUrl = appConfig?.authPageUrl ?? '/'

            // Використовуємо navigationService для навігації без перезавантаження сторінки
            // Якщо navigationService готовий (React Router ініціалізовано)
            if (navigationService.isReady()) {
                navigationService.navigateTo(authPageUrl, { replace: true })
            } else {
                // Fallback: якщо navigationService ще не ініціалізовано (напр., під час SSR)
                // Використовуємо window.location.href з replace для того, щоб не залишати
                // вихідну сторінку в історії браузера
                window.location.replace(authPageUrl)
            }
        }
    }

    /**
     * Відновлення пароля
     * Відправляє запит на відновлення пароля на вказану email-адресу
     * @param data - дані для відновлення (email або логін)
     * @returns результат операції
     */
    async forgotPassword(data: IForgotPasswordData): Promise<IForgotPasswordResult> {
        try {
            const apiConfig = this.getApiConfig()

            const response = await restClient.post(apiConfig.forgotPasswordUrl, {
                json: { email: data.login },
                headers: {
                    'Content-Type': 'application/ld+json',
                },
            })

            // Перевіряємо на помилку Hydra
            if (
                response &&
                typeof response === 'object' &&
                '@type' in response &&
                response['@type'] === 'hydra:Error'
            ) {
                return {
                    success: false,
                    error: 'User with such E-Mail not found',
                }
            }

            return {
                success: true,
                token: response?.token ?? undefined,
            }
        } catch (error: any) {
            if (this.debug) {
                console.error('[UserManager] Forgot password error:', error)
            }

            // Extract violations from backend 422 response
            const responseData = error?.response
            if (responseData?.violations?.length) {
                const errorMessage = responseData.violations
                    .map((v: { field: string; message: string }) => v.message)
                    .join(' ')
                return { success: false, error: errorMessage }
            }
            if (responseData?.message) {
                return { success: false, error: responseData.message }
            }

            return {
                success: false,
                error: error?.message || 'Unknown error',
            }
        }
    }

    /**
     * Підтвердження OTP і встановлення нового пароля
     * @param data - токен, OTP-код та новий пароль
     * @returns результат операції
     */
    async resetPassword(data: IResetPasswordData): Promise<IResetPasswordResult> {
        try {
            const apiConfig = this.getApiConfig()
            const url = apiConfig.resetPasswordUrl.replace('{token}', encodeURIComponent(data.token))

            await restClient.patch(url, {
                json: { code: data.code, plainPassword: data.plainPassword },
                headers: { 'Content-Type': 'application/json' },
            })

            return { success: true }
        } catch (error: any) {
            if (this.debug) {
                console.error('[UserManager] Reset password error:', error)
            }

            const responseData = error?.response
            if (responseData?.violations?.length) {
                const errorMessage = responseData.violations
                    .map((v: { field: string; message: string }) => v.message)
                    .join(' ')
                return { success: false, error: errorMessage }
            }
            if (responseData?.message) {
                return { success: false, error: responseData.message }
            }

            return { success: false, error: error?.message || 'Unknown error' }
        }
    }

    /**
     * Оновлення JWT токена за допомогою refresh токена
     * Автоматично викликається при отриманні 401 помилки
     * @returns чи успішне оновлення
     */
    async refreshAccessToken(): Promise<boolean> {
        // Очікуємо завершення гідратації перед перевіркою токенів
        if (!this.isHydrated) {
            await this.waitForHydration()
        }

        // Якщо вже йде процес оновлення — повертаємо той самий Promise.
        // Це вирішує race condition: паралельні 401-запити не відміняють один одному retry,
        // а всі чекають одного рефрешу і отримують однаковий результат true/false.
        if (this.isRefreshing) {
            if (this.refreshPromise) {
                return this.refreshPromise
            }
            // Захисний fallback (не повинен спрацювати за нормального потоку)
            console.warn('[UserManager] Token refresh in progress but no promise found, skipping')
            return false
        }

        // Якщо немає refreshToken, одразу викликаємо logout (один раз)
        if (!this.refreshToken) {
            console.warn('[UserManager] No refresh token available, logging out')
            // Встановлюємо прапорець щоб інші запити не намагалися оновити
            this.isRefreshing = true
            this.logout()
            return false
        }

        this.isRefreshing = true

        this.refreshPromise = (async () => {
            try {
                const apiConfig = this.getApiConfig()
                const refreshData = await restClient.post<IRefreshTokenResponse>(
                    apiConfig.refreshTokenUrl,
                    {
                        json: {
                            refresh_token: this.refreshToken,
                        },
                    },
                    { isPublic: true }
                )
                if (!refreshData || !refreshData.token) {
                    throw new Error('No response or token from refresh endpoint')
                }
                this.setToken(refreshData.token)
                this.setCentrifugoConnectionToken(refreshData.centrifugo_connection_token ?? null)
                this.setCentrifugoConnectionTokenExpiration(refreshData.centrifugo_connection_token_expiration ?? null)
                this.setCentrifugoSubscriptionToken(refreshData.centrifugo_subscription_token ?? null)
                this.setCentrifugoSubscriptionTokenExpiration(refreshData.centrifugo_subscription_token_expiration ?? null)
                this.setRefreshToken(refreshData.refresh_token)
                this.setRefreshTokenExpiration(refreshData.refresh_token_expiration)

                return true
            } catch (error: any) {
                console.error('[UserManager] Refresh token error:', error)

                // Якщо не вдалося оновити токен - виходимо з системи
                this.logout()
                return false
            } finally {
                this.isRefreshing = false
                this.refreshPromise = null
            }
        })()

        return this.refreshPromise
    }

    /**
     * Отримання опції користувача
     * @param name - назва опції
     * @param defaultValue - значення за замовчуванням
     * @returns значення опції або defaultValue
     */
    getUserOption<T = any>(name: string, defaultValue?: T): T | undefined {
        const storedOption = this.userOptions[name]

        if (storedOption !== undefined) {
            return storedOption
        }

        return defaultValue
    }

    /**
     * Чи можна синхронізувати код опції на сервер.
     * Бекенд дозволяє лише символи [a-z0-9_-] завдовжки 1..50.
     * @param code - код опції
     */
    private isSyncableOptionCode(code: string): boolean {
        return /^[a-z0-9_-]{1,50}$/.test(code)
    }

    /**
     * Встановлення опції користувача
     * Зберігає опцію локально (реактивно) та асинхронно відправляє на сервер.
     * Локальне значення застосовується миттєво — помилка мережі не ламає UX.
     * @param name - назва опції
     * @param value - значення опції
     * @param sync - чи відправляти на сервер (за замовчуванням true; false — лише кеш)
     */
    async setUserOption(name: string, value: any, sync: boolean = true): Promise<void> {
        // Зберігаємо локально
        this.userOptions[name] = value

        // Наповнення кешу з сервера — без зворотної відправки
        if (!sync) return

        // Бекенд приймає код лише з [a-z0-9_-] (1..50); легасі-коди (напр. isFilterOpen)
        // лишаємо локальними, щоб не отримувати 422
        if (!this.isSyncableOptionCode(name)) return

        try {
            const apiConfig = this.getApiConfig()

            // Відправляємо на сервер (upsert за кодом)
            await restClient.post(apiConfig.userOptionUrl, {
                json: {
                    code: name,
                    value,
                },
            })
        } catch (error: any) {
            if (this.debug) {
                console.error('[UserManager] Failed to save user option:', error)
            }
        }
    }

    /**
     * Батчеве завантаження опцій користувача з сервера за списком кодів.
     * Отримані значення наповнюють локальний кеш через setUserOption(sync: false),
     * тобто без зворотного POST. Використовується при першому відкритті ресурсу.
     * @param codes - список кодів опцій
     */
    async loadUserOptions(codes: string[]): Promise<void> {
        const validCodes = (Array.isArray(codes) ? codes : []).filter((code) => this.isSyncableOptionCode(code))
        if (validCodes.length === 0) return

        const apiConfig = this.getApiConfig()

        // Колекційний endpoint віддає лише метадані (без `value`), тож тягнемо кожну
        // опцію окремим GET за кодом. isDisableResponseCache обов'язковий: URL опції
        // однаковий для різних користувачів, і кеш GET-відповідей restClient віддав би
        // відповідь попереднього користувача.
        await Promise.all(
            validCodes.map(async (code) => {
                try {
                    const option: any = await restClient.get(
                        `${apiConfig.userOptionUrl}/${encodeURIComponent(code)}`,
                        undefined,
                        { isDisableResponseCache: true }
                    )
                    if (option?.value !== undefined) {
                        // Наповнюємо кеш без зворотного POST
                        this.setUserOption(code, option.value, false)
                    }
                } catch (error: any) {
                    // 404 — опції ще немає; тихо працюємо на дефолтах
                    if (this.debug) {
                        console.error('[UserManager] Failed to load user option:', code, error)
                    }
                }
            })
        )
    }

    /**
     * Резолв опцій користувача на сервері за ланцюгом fallback
     * «персональна → глобальний дефолт (default_<code>) → null».
     *
     * Клієнт передає ЗВИЧАЙНІ коди (без префікса default_) — префікс додає сервер.
     * Отримані значення наповнюють кеш userOptions без зворотного POST; для
     * source='none' (value=null) ключ видаляється з кешу, і UI падає на базовий
     * набір ресурсу (останній крок fallback лишається на клієнті).
     *
     * Виклики від різних компонентів, що потрапили в одне setTimeout(0)-вікно,
     * СКЛЕЮЮТЬСЯ в один HTTP-запит з об'єднаним списком кодів (ціль — ~1 запит
     * на сторінку). Коди, що вже завантажуються, повторно не запитуються —
     * викликач чекає їхній inflight-батч.
     *
     * @param codes - список кодів опцій (звичайні, без default_)
     * @param options.force - примусовий запит повз inflight-дедуп (після
     *                        збереження/видалення опції чи дефолту)
     */
    async resolveUserOptions(codes: string[], options: { force?: boolean } = {}): Promise<void> {
        const validCodes = (Array.isArray(codes) ? codes : []).filter((code) => this.isSyncableOptionCode(code))
        if (validCodes.length === 0) return

        const waits = new Set<Promise<void>>()

        for (const code of validCodes) {
            const inflight = this.inflightResolves.get(code)
            if (inflight && !options.force) {
                waits.add(inflight)
                continue
            }

            this.pendingResolveCodes.add(code)
            const flush = this.scheduleResolveFlush()
            this.inflightResolves.set(code, flush)
            waits.add(flush)
        }

        await Promise.all(waits)
    }

    /**
     * Планує флаш накопичених кодів одним resolved-запитом у наступному макротаску.
     * setTimeout(0), а не мікротаск: continuation'и після await waitForHydration()
     * у різних компонентів можуть розкидатися по сусідніх мікротасках, а вікно
     * макротаска гарантовано збирає їх в один батч.
     */
    private scheduleResolveFlush(): Promise<void> {
        if (this.resolveFlushPromise) {
            return this.resolveFlushPromise
        }

        const flush: Promise<void> = new Promise((resolve) => {
            setTimeout(() => {
                this.resolveFlushPromise = null
                const batch = Array.from(this.pendingResolveCodes)
                this.pendingResolveCodes.clear()

                void this.fetchResolvedOptions(batch).finally(() => {
                    for (const code of batch) {
                        if (this.inflightResolves.get(code) === flush) {
                            this.inflightResolves.delete(code)
                        }
                    }
                    resolve()
                })
            }, 0)
        })

        this.resolveFlushPromise = flush
        return flush
    }

    /**
     * Виконує resolved-запит(и) для батча кодів та застосовує відповідь до кешу.
     * Бекенд приймає до 100 кодів на запит — більші батчі чанкуються.
     * isDisableResponseCache обов'язковий: URL однаковий для різних користувачів,
     * і кеш GET-відповідей restClient віддав би відповідь попереднього користувача.
     */
    private async fetchResolvedOptions(codes: string[]): Promise<void> {
        if (codes.length === 0) return

        const apiConfig = this.getApiConfig()

        const MAX_CODES_PER_REQUEST = 100
        const chunks: string[][] = []
        for (let i = 0; i < codes.length; i += MAX_CODES_PER_REQUEST) {
            chunks.push(codes.slice(i, i + MAX_CODES_PER_REQUEST))
        }

        await Promise.all(
            chunks.map(async (chunk) => {
                try {
                    const response: any = await restClient.get(
                        `${apiConfig.resolvedUserOptionUrl}?codes=${chunk.map(encodeURIComponent).join(',')}`,
                        undefined,
                        { isDisableResponseCache: true }
                    )

                    const items: IResolvedUserOption[] = Array.isArray(response?.items) ? response.items : []
                    for (const item of items) {
                        if (!item || typeof item.code !== 'string') continue
                        this.applyResolvedOption(item.code, item.value)
                    }
                } catch (error: any) {
                    // Мережа/сервер недоступні — тихо працюємо на локальному кеші й дефолтах
                    if (this.debug) {
                        console.error('[UserManager] Failed to resolve user options:', chunk, error)
                    }
                }
            })
        )
    }

    /**
     * Застосовує зрезолвлене сервером значення до кешу (MobX action).
     * null означає «немає ні персональної, ні дефолту» — ключ видаляється,
     * щоб споживачі впали на базовий набір, а не отримали null-значення.
     */
    private applyResolvedOption(code: string, value: any): void {
        if (value === null || value === undefined) {
            if (this.userOptions[code] !== undefined) {
                delete this.userOptions[code]
            }
            return
        }

        this.userOptions[code] = value
    }

    /**
     * Збереження глобальної опції (дефолт ресурсу для всіх користувачів).
     * Код передається З префіксом default_ (напр. default_tablecolumns_orders).
     * Доступно лише адміну; 403 для не-адміна та мережеві помилки ковтаються —
     * основний бар'єр прав на бекенді, локальний UX не ламається.
     * @param code - код глобальної опції (з префіксом default_)
     * @param value - значення опції
     */
    async setGlobalOption(code: string, value: any): Promise<void> {
        if (!this.isSyncableOptionCode(code)) return

        try {
            const apiConfig = this.getApiConfig()

            // Upsert за кодом
            await restClient.post(apiConfig.optionUrl, {
                json: {
                    code,
                    value,
                },
            })
        } catch (error: any) {
            if (this.debug) {
                console.error('[UserManager] Failed to save global option:', error)
            }
        }
    }

    /**
     * Видалення глобальної опції (дефолту ресурсу). Лише адмін; помилки ковтаються.
     * @param code - код глобальної опції (з префіксом default_)
     */
    async deleteGlobalOption(code: string): Promise<void> {
        if (!this.isSyncableOptionCode(code)) return

        try {
            const apiConfig = this.getApiConfig()

            await restClient.delete(`${apiConfig.optionUrl}/${encodeURIComponent(code)}`)
        } catch (error: any) {
            if (this.debug) {
                console.error('[UserManager] Failed to delete global option:', error)
            }
        }
    }

    /**
     * Видалення опції користувача локально (реактивно) та на сервері.
     * @param name - назва опції
     */
    async deleteUserOption(name: string): Promise<void> {
        // Видаляємо локально (реактивно)
        delete this.userOptions[name]

        // Несумісні коди на сервер не потрапляли — видаляти нічого
        if (!this.isSyncableOptionCode(name)) return

        try {
            const apiConfig = this.getApiConfig()

            await restClient.delete(`${apiConfig.userOptionUrl}/${encodeURIComponent(name)}`)
        } catch (error: any) {
            if (this.debug) {
                console.error('[UserManager] Failed to delete user option:', error)
            }
        }
    }

    /**
     * Перевірка наявності ролі у користувача
     * @param role - роль для перевірки
     * @returns чи має користувач цю роль
     */
    hasRole(role: string): boolean {
        if (!this.user.roles || !Array.isArray(this.user.roles)) {
            return false
        }

        return this.user.roles.includes(role)
    }

    /**
     * Перевірка наявності будь-якої з ролей у користувача
     * @param roles - масив ролей для перевірки
     * @returns чи має користувач хоча б одну з цих ролей
     */
    hasAnyRole(roles: string[]): boolean {
        if (!this.user.roles || !Array.isArray(this.user.roles)) {
            return false
        }

        return roles.some((role) => this.user.roles?.includes(role))
    }

    // Методи для встановлення значень (використовуються внутрішньо та зовнішньо)

    /**
     * Встановлює JWT токен
     * @param token - JWT токен
     */
    setToken(token: string): void {
        this.token = token
    }

    setCentrifugoConnectionToken(token: string | null): void {
        this.centrifugoConnectionToken = token
    }

    setCentrifugoConnectionTokenExpiration(exp: string | null): void {
        this.centrifugoConnectionTokenExpiration = exp
    }

    setCentrifugoSubscriptionToken(token: string | null): void {
        this.centrifugoSubscriptionToken = token
    }

    setCentrifugoSubscriptionTokenExpiration(exp: string | null): void {
        this.centrifugoSubscriptionTokenExpiration = exp
    }

    /**
     * Встановлює Mercure токен
     * @param mercureToken - Mercure токен
     */
    setMercureToken(mercureToken: string | null): void {
        this.mercureToken = mercureToken
    }

    /**
     * Встановлює refresh токен
     * @param refreshToken - refresh токен
     */
    setRefreshToken(refreshToken: string): void {
        this.refreshToken = refreshToken
    }

    /**
     * Встановлює час закінчення дії refresh токена
     * @param refreshTokenExpiration - час закінчення дії
     */
    setRefreshTokenExpiration(refreshTokenExpiration: string): void {
        this.refreshTokenExpiration = refreshTokenExpiration
    }

    /**
     * Встановлює прапорець авторизації
     * @param isAuthorized - чи авторизований користувач
     */
    private setIsAuthorized(isAuthorized: boolean): void {
        this.isAuthorized = isAuthorized
    }

    /**
     * Встановлює дані користувача
     * @param user - дані користувача
     */
    private setUser(user: IUser): void {
        this.user = user
    }

    /**
     * Очищає всі дані користувача та токени
     * Також очищає всі контексти CRUD ресурсів з registry
     */
    clear(): void {
        this.isAuthorized = false
        this.mustChangePassword = false
        this.user = {}
        this.token = null
        this.centrifugoConnectionToken = null
        this.centrifugoConnectionTokenExpiration = null
        this.centrifugoSubscriptionToken = null
        this.centrifugoSubscriptionTokenExpiration = null
        this.refreshToken = null
        this.refreshTokenExpiration = null

        // Спочатку відключаємо персистентність (щоб обнулення не затерло бакет
        // користувача в localStorage), потім чистимо опції в пам'яті
        this.detachUserOptionsPersistence()
        this.userOptions = {}

        // Очищуємо всі контексти CRUD ресурсів при виході користувача
        // Це важливо для безпеки та коректної роботи при зміні користувача
        try {
            crudResourceRegistry.clearAll()
            
            if (this.debug) {
                console.log('[UserManager] Очищено всі контексти CRUD ресурсів')
            }
        } catch (error) {
            console.error('[UserManager] Помилка при очищенні контекстів CRUD ресурсів:', error)
        }
    }

    /**
     * Виводить інформацію про стан менеджера (для налагодження)
     */
    debugInfo(): void {
        if (!this.debug) return

        console.group('[UserManager] Debug Info')
        console.log('Is Authorized:', this.isAuthorized)
        console.log('User:', toJS(this.user))
        console.log('Has Token:', !!this.token)
        console.log('Has Refresh Token:', !!this.refreshToken)
        console.log('User Options:', toJS(this.userOptions))
        console.groupEnd()
    }
}

/**
 * Глобальний екземпляр UserManager
 * Використовується в усьому додатку
 */
export const userManager = new UserManager({ debug: true })
